import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  getAutopayStatus,
  startAutopaySetup,
  completeAutopaySetup,
  revokeAutopay,
} from '../../api/studentWallet';
import {
  fetchChildAutopayStatus,
  startChildAutopaySetup,
  completeChildAutopaySetup,
  revokeChildAutopay,
} from '../../api/studentPortal';
import { apiErrorMessage } from '../../utils/apiError';

async function loadRazorpayScript() {
  if (window.Razorpay) return;
  await new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

/**
 * Bank auto-pay: link / unlink ACH, PAD, or UPI Autopay when enabled for the academy.
 */
export default function AutoPayPanel({ isParentView, childStudentId }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const data = isParentView
        ? await fetchChildAutopayStatus(childStudentId)
        : await getAutopayStatus();
      setStatus(data);
    } catch (e) {
      setStatus(null);
      setError(apiErrorMessage(e, t('pages.wallet.autopayLoadFailed', { defaultValue: 'Could not load auto-pay status.' })));
    } finally {
      setLoading(false);
    }
  }, [isParentView, childStudentId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const autopay = searchParams.get('autopay');
    const sessionId = searchParams.get('session_id');
    if (autopay !== 'success' || !sessionId) return;

    let cancelled = false;
    (async () => {
      setBusy(true);
      setError('');
      try {
        const data = isParentView
          ? await completeChildAutopaySetup(childStudentId, sessionId)
          : await completeAutopaySetup(sessionId);
        if (!cancelled) {
          setStatus(data);
          setInfo(t('pages.wallet.autopayLinked', { defaultValue: 'Bank auto-pay is linked.' }));
        }
      } catch (e) {
        if (!cancelled) {
          setError(apiErrorMessage(e, t('pages.wallet.autopayCompleteFailed', { defaultValue: 'Could not confirm bank auto-pay.' })));
        }
      } finally {
        if (!cancelled) setBusy(false);
        const next = new URLSearchParams(searchParams);
        next.delete('autopay');
        next.delete('gateway');
        next.delete('session_id');
        setSearchParams(next, { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams, isParentView, childStudentId, t]);

  if (loading) return null;
  if (!status?.featureEnabled) return null;
  if (!status.availableForCountry && status.status !== 'ACTIVE' && status.status !== 'PENDING_SETUP') {
    return null;
  }

  const linked = status.status === 'ACTIVE';
  const pending = status.status === 'PENDING_SETUP';

  const finishRazorpay = async (orderId, paymentId) => {
    const data = isParentView
      ? await completeChildAutopaySetup(childStudentId, orderId, paymentId)
      : await completeAutopaySetup(orderId, paymentId);
    setStatus(data);
    setInfo(t('pages.wallet.autopayLinked', { defaultValue: 'Bank auto-pay is linked.' }));
  };

  const openRazorpayAuth = async (res) => {
    await loadRazorpayScript();
    const rzp = new window.Razorpay({
      key: res.keyId || res.publishableKey,
      amount: res.amountMinor,
      currency: res.currency || 'INR',
      order_id: res.orderId || res.sessionId,
      customer_id: res.customerId,
      recurring: '1',
      name: t('pages.wallet.autopayTitle', { defaultValue: 'Bank auto-pay' }),
      description: t('pages.wallet.autopayRazorpayDesc', {
        defaultValue: 'Authorize UPI Autopay for academy fees',
      }),
      theme: { color: '#4f46e5' },
      handler: async (response) => {
        try {
          await finishRazorpay(
            response.razorpay_order_id || res.orderId || res.sessionId,
            response.razorpay_payment_id,
          );
        } catch (e) {
          setError(apiErrorMessage(e, t('pages.wallet.autopayCompleteFailed', { defaultValue: 'Could not confirm bank auto-pay.' })));
        } finally {
          setBusy(false);
        }
      },
      modal: {
        ondismiss: () => setBusy(false),
      },
    });
    rzp.open();
  };

  const onSetup = async () => {
    if (!consentAccepted) {
      setError(t('pages.wallet.autopayConsentRequired', {
        defaultValue: 'Please accept the auto-pay authorization before continuing.',
      }));
      return;
    }
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const res = isParentView
        ? await startChildAutopaySetup(childStudentId)
        : await startAutopaySetup();
      if (res?.provider === 'RAZORPAY' || (!res?.checkoutUrl && (res?.orderId || res?.sessionId))) {
        await openRazorpayAuth(res);
        return;
      }
      if (res?.checkoutUrl) {
        window.location.href = res.checkoutUrl;
        return;
      }
      throw new Error(t('pages.wallet.autopaySetupFailed', { defaultValue: 'Could not start bank auto-pay setup.' }));
    } catch (e) {
      setError(apiErrorMessage(e, t('pages.wallet.autopaySetupFailed', { defaultValue: 'Could not start bank auto-pay setup.' })));
      setBusy(false);
    }
  };

  const onRevoke = async () => {
    if (!window.confirm(t('pages.wallet.autopayRevokeConfirm', {
      defaultValue: 'Stop bank auto-pay? Future fees will need a wallet top-up.',
    }))) return;
    setBusy(true);
    setError('');
    try {
      const data = isParentView
        ? await revokeChildAutopay(childStudentId)
        : await revokeAutopay();
      setStatus(data);
      setInfo(t('pages.wallet.autopayRevoked', { defaultValue: 'Bank auto-pay has been turned off.' }));
      setConsentAccepted(false);
    } catch (e) {
      setError(apiErrorMessage(e, t('pages.wallet.autopayRevokeFailed', { defaultValue: 'Could not turn off bank auto-pay.' })));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="mb-1 text-base font-semibold text-gray-900">
        {t('pages.wallet.autopayTitle', { defaultValue: 'Bank auto-pay' })}
      </h2>
      <p className="mb-3 text-xs text-gray-500">
        {t('pages.wallet.autopayHint', {
          defaultValue: 'When your wallet is short, we can pull the shortfall from a linked bank account (clears in a few business days).',
        })}
      </p>

      {status.badgeLabel && (
        <p className="mb-3 text-xs font-medium text-indigo-700">{status.badgeLabel}</p>
      )}

      {status.pullTiming === 'AFTER_GRACE_DAYS' && (
        <p className="mb-3 text-xs text-slate-600">
          {t('pages.wallet.autopayGraceHint', {
            defaultValue: 'Bank pulls start after {{days}} unpaid collection day(s).',
            days: status.graceDays ?? 2,
          })}
        </p>
      )}

      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}
      {info && (
        <div className="mb-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{info}</div>
      )}

      {linked && (
        <div className="mb-3 text-sm text-gray-700">
          {t('pages.wallet.autopayLinkedDetail', {
            defaultValue: 'Linked: {{bank}} •••• {{lastFour}}',
            bank: status.bankName || t('pages.wallet.autopayBank', { defaultValue: 'Bank account' }),
            lastFour: status.lastFour || '????',
          })}
          {status.hasPendingCharge && (
            <p className="mt-1 text-xs text-amber-700">
              {t('pages.wallet.autopayPendingCharge', {
                defaultValue: 'A bank debit is still clearing.',
              })}
            </p>
          )}
        </div>
      )}

      {pending && !linked && (
        <p className="mb-3 text-sm text-amber-800">
          {t('pages.wallet.autopayPendingSetup', {
            defaultValue: 'Setup started but not finished. You can try again.',
          })}
        </p>
      )}

      {!linked && status.consentText && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-700">
          <p className="font-semibold text-slate-800">
            {status.consentSummary || t('pages.wallet.autopayConsentTitle', { defaultValue: 'Authorization' })}
          </p>
          <p className="mt-2 whitespace-pre-wrap leading-relaxed">{status.consentText}</p>
          {status.consentVersion && (
            <p className="mt-2 text-[11px] text-slate-500">
              {t('pages.wallet.autopayConsentVersion', {
                defaultValue: 'Consent version {{version}}',
                version: status.consentVersion,
              })}
            </p>
          )}
          <label className="mt-3 flex items-start gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              className="mt-1"
              checked={consentAccepted}
              onChange={(e) => setConsentAccepted(e.target.checked)}
              disabled={busy}
            />
            <span>
              {t('pages.wallet.autopayConsentCheckbox', {
                defaultValue: 'I authorize bank auto-pay as described above.',
              })}
            </span>
          </label>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!linked && (
          <button
            type="button"
            disabled={busy || !consentAccepted}
            onClick={onSetup}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {busy
              ? t('common.pleaseWait', { defaultValue: 'Please wait…' })
              : t('pages.wallet.autopayLink', { defaultValue: 'Link bank account' })}
          </button>
        )}
        {linked && (
          <button
            type="button"
            disabled={busy}
            onClick={onRevoke}
            className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {t('pages.wallet.autopayTurnOff', { defaultValue: 'Turn off auto-pay' })}
          </button>
        )}
      </div>
    </div>
  );
}
